"use client";

import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { importLibrary, setOptions } from "@googlemaps/js-api-loader";

import Header from "../components/Header";
import Leftnav from "../components/Leftnav";
import Rightchat from "../components/Rightchat";
import Appfooter from "../components/Appfooter";
import Popupchat from "../components/Popupchat";

import { getClubsApi } from "../api/ClientActions";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

if (GOOGLE_MAPS_API_KEY) {
  setOptions({
    key: GOOGLE_MAPS_API_KEY,
    v: "weekly",
    language: "en",
    region: "IN",
  });
}

interface ClubMapItem {
  client_id?: number | string;
  id?: number | string;
  unique_slug?: string;
  client_name?: string;
  club_name?: string;
  organization_name?: string;
  name?: string;
  location_address?: string;
  latitude?: string | number | null;
  longitude?: string | number | null;
  contact_email?: string;
  client_email?: string;
  email?: string;
  mobile_number?: string;
  phone_number?: string;
  phone?: string;
  mobile?: string;
  level_name?: string;
  total_members?: number;
  member_count?: number;
  total_followers?: number;
  follower_count?: number;
  coords?: [number, number];
}

const escapeHtml = (value: any) => {
  return String(value || "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
};

const getClubName = (club: ClubMapItem) => {
  return (
    club?.client_name ||
    club?.club_name ||
    club?.organization_name ||
    club?.name ||
    "Club"
  );
};

const getClubSlug = (club: ClubMapItem) => {
  return String(club?.unique_slug || club?.client_id || club?.id || "").trim();
};

const getClubProfilePath = (club: ClubMapItem) => {
  const slug = getClubSlug(club);
  return slug ? `/club/${encodeURIComponent(slug)}` : "#";
};

const getClubCoords = (club: ClubMapItem): [number, number] | null => {
  const lat = club?.latitude;
  const lng = club?.longitude;

  if (
    lat === null ||
    lat === undefined ||
    lng === null ||
    lng === undefined ||
    lat === "" ||
    lng === ""
  ) {
    return null;
  }

  const numLat = Number(lat);
  const numLng = Number(lng);

  if (Number.isNaN(numLat) || Number.isNaN(numLng)) {
    return null;
  }

  return [numLat, numLng];
};

const ClubIndiaMap: React.FC = () => {
  const mapDivRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const initTimerRef = useRef<number | null>(null);
  const searchMarkerRef = useRef<google.maps.Marker | null>(null);

  const [locationSearch, setLocationSearch] = useState("");
  const [searchingLocation, setSearchingLocation] = useState(false);
  const [locationSearchError, setLocationSearchError] = useState("");

  const [clients, setClients] = useState<ClubMapItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchClubs = async () => {
      try {
        setLoading(true);

        const result = await getClubsApi();

        if (cancelled) {
          return;
        }

        if (
          result?.success === false ||
          result?.status === false
        ) {
          setClients([]);
          return;
        }

        const clubList =
          Array.isArray(result?.data)
            ? result.data
            : Array.isArray(result?.clients)
            ? result.clients
            : [];

        setClients(clubList);
      } catch (error) {
        if (!cancelled) {
          console.error(
            "Club map fetch error:",
            error
          );

          setClients([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchClubs();

    return () => {
      cancelled = true;
    };
  }, []);

  const mapClubs = useMemo(() => {
    return (clients || [])
      .map((club: ClubMapItem) => ({
        ...club,
        coords: getClubCoords(club),
      }))
      .filter(
        (
          club
        ): club is ClubMapItem & {
          coords: [number, number];
        } => club.coords !== null
      );
  }, [clients]);

  useEffect(() => {
    const container = mapDivRef.current;

    if (!container || loading || !mapClubs.length || !GOOGLE_MAPS_API_KEY) {
      return undefined;
    }

    let cancelled = false;

    if (initTimerRef.current !== null) {
      window.clearTimeout(initTimerRef.current);
      initTimerRef.current = null;
    }

    const initializeGoogleMap = async () => {
      try {
        const { Map } = (await importLibrary(
          "maps",
        )) as google.maps.MapsLibrary;

        if (cancelled || !mapDivRef.current) return;

        markersRef.current.forEach((marker) => marker.setMap(null));
        markersRef.current = [];

        if (infoWindowRef.current) {
          infoWindowRef.current.close();
        }

        const firstCoords = mapClubs[0]?.coords || [22.9734, 78.6569];

        const map = new Map(mapDivRef.current, {
          center: {
            lat: firstCoords[0],
            lng: firstCoords[1],
          },
          zoom: 6,
          minZoom: 2,
          maxZoom: 18,
          mapTypeControl: true,
          streetViewControl: true,
          fullscreenControl: true,
          zoomControl: true,
          gestureHandling: "cooperative",
        });

        mapInstanceRef.current = map;

        const infoWindow = new google.maps.InfoWindow();
        infoWindowRef.current = infoWindow;
        const bounds = new google.maps.LatLngBounds();

        mapClubs.forEach((club: ClubMapItem) => {
          if (!club.coords) return;

          const position = {
            lat: club.coords[0],
            lng: club.coords[1],
          };

          bounds.extend(position);

          const marker = new google.maps.Marker({
            map,
            position,
            title: getClubName(club),
          });

          const clubName = escapeHtml(getClubName(club));
          const category = escapeHtml(club?.level_name || "Club");
          const address = escapeHtml(
            club?.location_address || "Location not updated",
          );
          const email = escapeHtml(
            club?.contact_email || club?.client_email || club?.email,
          );
          const clubProfilePath = getClubProfilePath(club);

          marker.addListener("click", () => {
            infoWindow.setContent(`
              <div style="min-width:260px;max-width:320px;font-family:Inter,Arial,sans-serif;padding:4px 2px 2px">
                <div style="display:flex;align-items:flex-start;gap:10px;margin-bottom:10px">
                  <div style="width:42px;height:42px;min-width:42px;border-radius:14px;background:#eef4ff;color:#2563eb;display:flex;align-items:center;justify-content:center;font-size:18px;font-weight:700">
                    ${clubName.charAt(0).toUpperCase()}
                  </div>

                  <div style="min-width:0;flex:1">
                    <a
                      href="${clubProfilePath}"
                      style="display:block;margin:0 0 4px;font-size:16px;font-weight:700;color:#0f172a;text-decoration:none;line-height:1.25"
                    >
                      ${clubName}
                    </a>

                    <span style="display:inline-flex;background:#eef4ff;color:#2563eb;border-radius:999px;padding:4px 9px;font-size:11px;font-weight:600">
                      ${category}
                    </span>
                  </div>
                </div>

                <div style="background:#f8fafc;border:1px solid #e6edf7;border-radius:14px;padding:10px 12px;margin-bottom:10px">
                  <p style="margin:0;font-size:12px;color:#475569;line-height:1.45">
                    ${address}
                  </p>

                  ${
                    email
                      ? `<p style="margin:8px 0 0;font-size:12px;color:#475569;line-height:1.4">
                          <strong style="color:#334155">Email:</strong> ${email}
                        </p>`
                      : ""
                  }

                  <p style="margin:8px 0 0;font-size:12px;color:#475569;line-height:1.4">
                    <strong style="color:#334155">Members:</strong> ${
                      club?.member_count || club?.total_members || 0
                    }
                  </p>
                </div>

                <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px">
                  <a
                    href="${clubProfilePath}"
                    style="height:34px;border-radius:999px;background:#2563eb;color:#ffffff;font-size:12px;font-weight:700;text-decoration:none;display:flex;align-items:center;justify-content:center"
                  >
                    View Club
                  </a>

                  <a
                    href="https://www.google.com/maps/search/?api=1&query=${position.lat},${position.lng}"
                    target="_blank"
                    rel="noopener noreferrer"
                    style="height:34px;border-radius:999px;background:#ffffff;border:1px solid #bfdbfe;color:#2563eb;font-size:12px;font-weight:700;text-decoration:none;display:flex;align-items:center;justify-content:center"
                  >
                    Google Maps
                  </a>
                </div>
              </div>
            `);

            infoWindow.open({
              map,
              anchor: marker,
            });
          });

          markersRef.current.push(marker);
        });

        if (mapClubs.length > 1) {
          map.fitBounds(bounds, 50);

          google.maps.event.addListenerOnce(map, "idle", () => {
            if ((map.getZoom() || 0) > 9) {
              map.setZoom(9);
            }
          });
        } else {
          map.setCenter({
            lat: firstCoords[0],
            lng: firstCoords[1],
          });
          map.setZoom(10);
        }
      } catch (error) {
        console.error("Failed to load Google Maps:", error);
      }
    };

    initTimerRef.current = window.setTimeout(() => {
      initTimerRef.current = null;
      initializeGoogleMap();
    }, 100);

    return () => {
      cancelled = true;

      if (initTimerRef.current !== null) {
        window.clearTimeout(initTimerRef.current);
        initTimerRef.current = null;
      }

      markersRef.current.forEach((marker) => marker.setMap(null));
      markersRef.current = [];

      if (searchMarkerRef.current) {
        searchMarkerRef.current.setMap(null);
        searchMarkerRef.current = null;
      }

      if (infoWindowRef.current) {
        infoWindowRef.current.close();
        infoWindowRef.current = null;
      }

      mapInstanceRef.current = null;
    };
  }, [loading, mapClubs]);


  const handleLocationSearch = async () => {
    const query = locationSearch.trim();

    if (!query) {
      setLocationSearchError("Enter a location to search.");
      return;
    }

    if (!mapInstanceRef.current) {
      setLocationSearchError("Map is not ready yet.");
      return;
    }

    try {
      setSearchingLocation(true);
      setLocationSearchError("");

      const { Geocoder } = (await importLibrary(
        "geocoding",
      )) as google.maps.GeocodingLibrary;

      const geocoder = new Geocoder();

      const response = await geocoder.geocode({
        address: query,
        componentRestrictions: {
          country: "IN",
        },
      });

      const result = response.results?.[0];

      if (!result?.geometry?.location) {
        setLocationSearchError("Location not found.");
        return;
      }

      const map = mapInstanceRef.current;
      const location = result.geometry.location;

      map.panTo(location);
      map.setZoom(14);

      if (searchMarkerRef.current) {
        searchMarkerRef.current.setMap(null);
      }

      searchMarkerRef.current = new google.maps.Marker({
        map,
        position: location,
        title: result.formatted_address || query,
        animation: google.maps.Animation.DROP,
      });
    } catch (error) {
      console.error("Location search failed:", error);
      setLocationSearchError("Unable to search this location.");
    } finally {
      setSearchingLocation(false);
    }
  };

  const handleLocationSearchKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleLocationSearch();
    }
  };

  return (
    <Fragment>
      <Header />
      <Leftnav />
      <Rightchat />

      <div className="main-content right-chat-active clubs-main-page">
        <div className="middle-sidebar-bottom clubs-middle-bottom">
          <div className="middle-sidebar-left clubs-middle-left">
            <div className="clubs-page-wrapper">
              <div className="clubs-header-card">
                <div className="clubs-header-top">
                  <div>
                    <span className="clubs-page-badge">
                      <i className="feather-map-pin" />
                      Club Map
                    </span>

                    <h2 className="clubs-title">Clubs Across India</h2>

                    <p className="clubs-subtitle">
                      Showing club locations across India.
                    </p>
                  </div>

                  <div className="clubs-header-stats">
                    <div>
                      <strong>{clients.length}</strong>
                      <span>Total Clubs</span>
                    </div>

                  </div>
                </div>
              </div>

              <div className="clubs-section-block">

                <div className="club-location-search">
                  <div className="club-location-search-box">
                    <i className="feather-search" />

                    <input
                      type="text"
                      value={locationSearch}
                      onChange={(event) => {
                        setLocationSearch(event.target.value);
                        setLocationSearchError("");
                      }}
                      onKeyDown={handleLocationSearchKeyDown}
                      placeholder="Search city, area or address"
                      aria-label="Search map location"
                    />

                    {locationSearch && (
                      <button
                        type="button"
                        className="club-location-clear"
                        onClick={() => {
                          setLocationSearch("");
                          setLocationSearchError("");

                          if (searchMarkerRef.current) {
                            searchMarkerRef.current.setMap(null);
                            searchMarkerRef.current = null;
                          }
                        }}
                        aria-label="Clear location search"
                      >
                        <i className="feather-x" />
                      </button>
                    )}

                    <button
                      type="button"
                      className="club-location-search-button"
                      onClick={handleLocationSearch}
                      disabled={searchingLocation || !GOOGLE_MAPS_API_KEY}
                    >
                      {searchingLocation ? "Searching..." : "Search"}
                    </button>
                  </div>

                  {locationSearchError && (
                    <p className="club-location-search-error">
                      {locationSearchError}
                    </p>
                  )}
                </div>

                <div className="clubs-section-header">
                  <div>
                    <h3>India Club Locations</h3>
                    <p>
                      Only clubs with valid map location data will appear on
                      this map.
                    </p>
                  </div>
                </div>

                {!GOOGLE_MAPS_API_KEY ? (
                  <div className="clubs-empty-card map-empty-card">
                    <div className="clubs-empty-icon">
                      <i className="feather-alert-circle" />
                    </div>

                    <h4>Google Maps API key is missing</h4>

                    <p>
                      Add REACT_APP_GOOGLE_MAPS_API_KEY to your .env file and
                      restart the app.
                    </p>
                  </div>
                ) : loading ? (
                  <div className="clubs-loader-card map-loader-card">
                    <div className="spinner-border text-primary" />
                    <p>Loading clubs map...</p>
                  </div>
                ) : mapClubs.length === 0 ? (
                  <div className="clubs-empty-card map-empty-card">
                    <div className="clubs-empty-icon">
                      <i className="feather-map-pin" />
                    </div>

                    <h4>No map location found</h4>

                    <p>
                      Please update club map location details to display clubs
                      on the map.
                    </p>
                  </div>
                ) : (
                  <div className="club-map-card">
                    <div ref={mapDivRef} className="club-google-map" />
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>

      <Popupchat />
      <Appfooter />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:wght@600;700&family=Inter:wght@400;500;600;700&display=swap');

        .clubs-main-page {
          width: 100%;
          min-height: 100vh;
          padding-top: 58px;
          padding-left: 285px;
          padding-right: 315px;
          overflow-x: hidden;
          font-family: 'Inter', sans-serif;
          background: linear-gradient(180deg, #f8f9fc 0%, #f1f3f8 100%);
        }

        .clubs-middle-bottom,
        .clubs-middle-left {
          width: 100%;
          max-width: 100%;
        }

        .clubs-page-wrapper {
          width: 100%;
          max-width: 900px;
          margin-left: 22px;
          margin-top: 28px;
          padding-bottom: 40px;
        }

        /* ==========================
           HEADER
        ========================== */

        .clubs-header-card {
          margin-bottom: 18px;
          padding: 14px 16px;
          border: 1px solid #e2eaf6;
          border-radius: 16px;
          background:
            linear-gradient(
              135deg,
              #ffffff 0%,
              #f7faff 58%,
              #f1f6ff 100%
            );
          box-shadow: 0 5px 16px rgba(10, 35, 84, 0.045);
        }

        .clubs-header-top {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
        }

        .clubs-page-badge {
          display: none;
        }

        .clubs-title {
          margin: 0 0 4px;
          color: #0A2354;
          font-family: 'Fraunces', serif;
          font-size: 24px;
          font-weight: 700;
          line-height: 1.25;
          letter-spacing: -0.01em;
        }

        .clubs-subtitle {
          margin: 0;
          color: #8a93a3;
          font-size: 13px;
          line-height: 1.5;
        }

        .clubs-header-stats {
          display: flex;
          align-items: center;
          gap: 7px;
          flex-shrink: 0;
        }

        .clubs-header-stats div {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          min-height: 26px;
          padding: 0 9px;
          border: 1px solid #dce7f7;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.78);
          white-space: nowrap;
        }

        .clubs-header-stats strong,
        .clubs-header-stats span {
          display: inline;
          margin: 0;
          color: #5f7fae;
          font-size: 11px;
          font-weight: 700;
          line-height: 1;
        }

        /* ==========================
           SECTION
        ========================== */

        .clubs-section-block {
          padding: 16px;
          margin-bottom: 18px;
          border: 1px solid #e9ecf2;
          border-radius: 16px;
          background: #ffffff;
          box-shadow: 0 2px 8px rgba(15, 23, 42, 0.045);
        }

        .clubs-section-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 14px;
        }

        .clubs-section-header h3 {
          margin: 0;
          color: #0A2354;
          font-family: 'Fraunces', serif;
          font-size: 18px;
          font-weight: 700;
          line-height: 1.3;
        }

        .clubs-section-header p {
          margin: 4px 0 0;
          color: #8a93a3;
          font-size: 12px;
          line-height: 1.5;
        }

        /* ==========================
           LOCATION SEARCH
        ========================== */

        .club-location-search {
          margin-bottom: 14px;
        }

        .club-location-search-box {
          position: relative;
          width: 100%;
          min-height: 40px;
          display: flex;
          align-items: center;
        }

        .club-location-search-box > i {
          position: absolute;
          left: 14px;
          top: 50%;
          z-index: 2;
          transform: translateY(-50%);
          color: #9aa4b2;
          font-size: 14px;
          pointer-events: none;
        }

        .club-location-search-box input {
          width: 100%;
          height: 40px;
          padding: 0 150px 0 36px;
          border: 1px solid #e5e8ef;
          border-radius: 10px;
          outline: none;
          color: #1f2937;
          background: #ffffff;
          font-size: 13px;
          box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.05);
          transition:
            border-color 0.2s ease,
            box-shadow 0.2s ease;
        }

        .club-location-search-box input::placeholder {
          color: #9aa4b2;
        }

        .club-location-search-box input:focus {
          border-color: #2F6FE4;
          box-shadow: 0 0 0 3px rgba(47, 111, 228, 0.10);
        }

        .club-location-clear {
          position: absolute;
          right: 104px;
          top: 50%;
          width: 18px;
          height: 18px;
          padding: 0;
          border: 0;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transform: translateY(-50%);
          color: #64748b;
          background: #eef1f6;
          font-size: 10px;
          cursor: pointer;
          z-index: 3;
        }

        .club-location-search-button {
          position: absolute;
          right: 4px;
          top: 4px;
          min-width: 92px;
          height: 32px;
          padding: 0 12px;
          border: 1px solid #2F6FE4;
          border-radius: 8px;
          color: #ffffff;
          background: #2F6FE4;
          font-size: 11px;
          font-weight: 700;
          cursor: pointer;
          z-index: 2;
          transition: 0.2s ease;
        }

        .club-location-search-button:hover:not(:disabled) {
          background: #1f57c7;
        }

        .club-location-search-button:disabled {
          opacity: 0.65;
          cursor: not-allowed;
        }

        .club-location-search-error {
          margin: 6px 2px 0;
          color: #dc2626;
          font-size: 10.5px;
        }

        /* ==========================
           MAP
        ========================== */

        .club-map-card {
          position: relative;
          width: 100%;
          height: 540px;
          min-height: 540px;
          overflow: hidden;
          border: 1px solid #e1e8f2;
          border-radius: 14px;
          background: #eef4ff;
          box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.35);
        }

        .club-google-map {
          width: 100% !important;
          height: 540px !important;
          min-height: 540px !important;
          display: block !important;
          position: relative !important;
        }

        /* ==========================
           EMPTY / LOADING
        ========================== */

        .clubs-loader-card,
        .clubs-empty-card {
          width: 100%;
          min-height: 280px;
          padding: 28px 22px;
          border: 1px dashed #dbe2ec;
          border-radius: 16px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          background: #ffffff;
        }

        .map-loader-card,
        .map-empty-card {
          min-height: 380px;
        }

        .clubs-loader-card p {
          margin: 10px 0 0;
          color: #94a3b8;
          font-size: 11.5px;
        }

        .clubs-empty-icon {
          width: 44px;
          height: 44px;
          margin: 0 auto 11px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #5f7fae;
          background: #eef4fb;
          font-size: 20px;
        }

        .clubs-empty-card h4 {
          margin: 0;
          color: #475569;
          font-size: 14px;
          font-weight: 700;
        }

        .clubs-empty-card p {
          max-width: 420px;
          margin: 6px auto 0;
          color: #94a3b8;
          font-size: 11.5px;
          line-height: 1.55;
        }

        /* ==========================
           GOOGLE MAP INFO WINDOW
        ========================== */

        .gm-style .gm-style-iw-c {
          border-radius: 14px !important;
          box-shadow: 0 14px 34px rgba(15, 23, 42, 0.16) !important;
        }

        .gm-style .gm-style-iw-d {
          overflow: auto !important;
        }

        /* ==========================
           RESPONSIVE
        ========================== */

        @media (max-width: 1400px) {
          .clubs-main-page {
            padding-right: 290px;
          }
        }

        @media (max-width: 1200px) {
          .clubs-main-page {
            padding-left: 20px;
            padding-right: 20px;
          }

          .clubs-page-wrapper {
            margin-left: 0;
            max-width: 100%;
          }
        }

        @media (max-width: 991px) {
          .club-map-card,
          .club-google-map {
            height: 480px !important;
            min-height: 480px !important;
          }
        }

        @media (max-width: 768px) {
          .clubs-main-page {
            padding-top: 16px;
            padding-left: 12px;
            padding-right: 12px;
          }

          .clubs-middle-bottom,
          .clubs-middle-left {
            padding-left: 0 !important;
            padding-right: 0 !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
          }

          .clubs-page-wrapper {
            margin-top: 0;
            margin-left: 0;
            max-width: 100%;
            padding-bottom: 76px;
          }

          .clubs-header-card {
            padding: 12px 13px;
            border-radius: 14px;
          }

          .clubs-title {
            font-size: 21px;
          }

          .clubs-section-block {
            padding: 13px;
            border-radius: 14px;
          }

          .club-map-card,
          .club-google-map {
            height: 430px !important;
            min-height: 430px !important;
          }
        }

        @media (max-width: 480px) {
          .clubs-header-top {
            flex-direction: column;
          }

          .clubs-header-stats {
            width: 100%;
          }

          .clubs-header-stats div {
            justify-content: center;
            flex: 1;
          }

          .club-location-search-box input {
            padding-right: 126px;
          }

          .club-location-clear {
            right: 88px;
          }

          .club-location-search-button {
            min-width: 76px;
            padding: 0 9px;
          }

          .club-map-card,
          .club-google-map {
            height: 400px !important;
            min-height: 400px !important;
          }
        }
      `}</style>
    </Fragment>
  );
};

export default ClubIndiaMap;
